import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { fileUploadService } from "@/services";

const FileUploadDropzone = ({ section, field, index, value, onChange, isFormLoading = false }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [tempFileData, setTempFileData] = useState(null);
  const [lastValidUrl, setLastValidUrl] = useState(null);
  
  // Function to handle file removal
  const handleRemoveFile = async (e) => {
    e.stopPropagation(); // Prevent dropzone from opening
    e.preventDefault(); // Prevent any default browser behavior
    
    // Simply update UI and notify parent
    setLastValidUrl(null);
    onChange(""); // Send empty string instead of null to ensure it's saved properly
  };
  
  // Validation function
  const validateFileType = (file) => {
    // Use the regex patterns from your fileUploadService
    const allowedTypes = {
      documents: /\.(docx|pdf)$/i,
      images: /\.(jpeg|jpg|png|gif)$/i
    };
    
    const fileName = file.name.toLowerCase();
    
    // Check if file extension matches any allowed type
    if (allowedTypes.documents.test(fileName) || allowedTypes.images.test(fileName)) {
      return true;
    }
    
    return false;
  };
  
  // Handle file upload
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    // Add console logging to check field ID
    console.log("FILE UPLOAD - Current field ID:", field.id);
    console.log("FILE UPLOAD - Field details:", {
      fieldId: field.id,
      fieldName: field.name,
      is48: field.id === 48 ? "YES" : "NO",
      sectionId: section.id
    });
    
    // Validate file type
    if (!validateFileType(file)) {
      setErrorMessage("Invalid file type. Please upload PDF, DOCX, JPEG, JPG, PNG, or GIF files only.");
      return;
    }
    
    console.log("------------ FILE UPLOAD PROCESS STARTED ------------");
    console.log("File dropped:", file.name, "size:", file.size, "type:", file.type);
    setIsUploading(true);
    setUploadProgress(0);
    setErrorMessage("");
    
    try {
      // Show uploading state but don't update form value yet
      // This prevents immediate save in parent component
      const tempFileData = {
        name: file.name,
        size: file.size,
        type: file.type,
        isUploading: true, // Add a flag to indicate file is being uploaded
      };
      
      // Only update the UI, but don't trigger parent onChange yet
      // We'll wait until upload is complete to call the actual onChange
      setTempFileData(tempFileData);
      
      // Get form ID and user ID from props
      const currentFormId = field.formId;
      const currentUserId = field.userId;
      
      console.log("Form context data:", {
        formId: currentFormId,
        userId: currentUserId,
        fieldId: field.id,
        sectionId: section.id,
        sectionRepeatIndex: index
      });
      
      // Verify token is available before upload
      const token = localStorage.getItem('token');
      console.log("Token available for upload:", token ? "Yes" : "No");
      
      try {
        console.log("Starting file upload service call");
        
        let uploadParams = {
          fileObj: file,
          fieldId: field.id,
          sectionId: section.id,
          repeatIndex: index,
          formId: currentFormId,
          userId: currentUserId
        };
        
        console.log("Using upload parameters:", uploadParams);
        
        // Upload the file with the correct parameters
        const response = await fileUploadService.uploadFile(
          uploadParams.fileObj, 
          uploadParams.fieldId, 
          uploadParams.sectionId, 
          uploadParams.repeatIndex,
          uploadParams.formId, 
          uploadParams.userId, 
          (progress) => {
            console.log(`Progress callback: ${progress}%`);
            setUploadProgress(progress);
          }
        );
        
        console.log("Upload complete, response:", response);
        
        // Simplify the URL structure
        const fileUrl = response.file_url || 
          `/files/${response.submission_id}/${encodeURIComponent(file.name)}`;
        console.log("Setting file URL:", fileUrl);
        
        // Make sure we're passing a defined value
        if (!fileUrl) {
          throw new Error("No file URL received from server");
        }
        
        // Store the URL as the last valid URL
        setLastValidUrl(fileUrl);
        onChange(fileUrl);
        
        if (response.status !== "success") {
          setErrorMessage("Error uploading file");
        }
      } catch (error) {
        console.error("Error in file upload process:", error);
        setErrorMessage("File could not be uploaded to server.");
      }
    } finally {
      console.log("------------ FILE UPLOAD PROCESS COMPLETED ------------");
      setIsUploading(false);
      setTempFileData(null);
    }
  }, [field.id, field.formId, field.userId, section.id, index, onChange]);
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false
  });

  // Handle file input change via Browse button
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log("File selected via browse:", file.name);
      onDrop([file]);
    }
  };
  
  // Process existing file data
  useEffect(() => {
    const loadExistingFile = async () => {
      console.log("Checking for existing file data:", value);
      
      // If form is still loading, don't process the value yet
      if (isFormLoading) {
        console.log("Form is still loading, waiting for data...");
        return;
      }

      // If value is null/undefined/empty but we have a lastValidUrl, don't restore it
      // This prevents unnecessary onChange calls
      if (!value && lastValidUrl) {
        console.log("Has lastValidUrl but no value - not restoring");
        return;
      }

      // If value is null/undefined/empty and no lastValidUrl, do nothing
      if (!value) {
        console.log("No file data present");
        return;
      }
      
      // If value is a string (URL), store it as lastValidUrl without triggering onChange
      if (typeof value === 'string') {
        console.log("Value is a URL string:", value);
        setLastValidUrl(value);
        return;
      }
      
      // If value is an object, convert to string URL and store it without triggering onChange
      if (typeof value === 'object') {
        console.log("Converting object to URL string");
        const fileUrl = value.url || 
          `/files/${value.submission_id || value.file_id}/${encodeURIComponent(value.name)}`;
        setLastValidUrl(fileUrl);
        // Remove the onChange call here to prevent auto-save
        // onChange(fileUrl);
      }
    };
    
    loadExistingFile();
  }, [value, isFormLoading, lastValidUrl]); // Remove onChange from dependencies

  // In your display logic, you can strip out the timestamp
  const getDisplayFileName = (url) => {
    if (!url) return "";
    const fullFileName = url.split('/').pop(); // Get the filename part
    return fullFileName.split('_').slice(1).join('_'); // Remove the timestamp prefix
  }

  // First, update the hasValidFileValue function to be more permissive
  const hasValidFileValue = (value) => {
    if (!value) return false;
    
    // If it's a string (URL), check if it's not empty
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    // If it's an object, check for any identifying property
    if (typeof value === 'object') {
      return Boolean(value.url || value.file_id || value.submission_id || value.name || value.type);
    }
    
    return false;
  };

  useEffect(() => {
    console.log('Current file value:', value);
    console.log('Last valid URL:', lastValidUrl);
    console.log('Has valid file value:', hasValidFileValue(value));
    console.log('Has valid last URL:', hasValidFileValue(lastValidUrl));
  }, [value, lastValidUrl]);

  return (
    <div>
      <div
        {...getRootProps()}
        className={`w-full px-4 py-3 rounded-lg border-2 ${
          isDragActive ? 'border-[#F16745] bg-[#F1EBDB]' : 
          isUploading ? 'border-[#F16745] bg-gray-50' : 
          'border-gray-300 bg-white'
        } flex items-center justify-between cursor-pointer relative overflow-visible`}
      >
        <input {...getInputProps()} onChange={handleFileInputChange} accept=".pdf,.docx,.jpeg,.jpg,.png,.gif" />
        
        {/* File info display with improved handling for backend files */}
        <div className="flex items-center flex-1 min-w-0 mr-24">
          {isFormLoading ? (
            // Show loading state when form data is still being fetched
            <div className="flex items-center text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
              <span>Loading file data...</span>
            </div>
          ) : (hasValidFileValue(value) || hasValidFileValue(lastValidUrl)) ? (
            <div className="flex items-center min-w-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-gray-700 truncate min-w-0 flex-1">
                {typeof (value || lastValidUrl) === 'string' 
                  ? getDisplayFileName(value || lastValidUrl)
                  : ((value?.name || lastValidUrl?.name) || "Uploaded file")}
              </span>
              
              {/* Add inline loading indicator next to filename when uploading */}
              {isUploading && (
                <div className="flex items-center ml-3 text-[#F16745] flex-shrink-0">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#F16745] mr-2"></div>
                  <span className="text-sm font-medium">{uploadProgress}%</span>
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-500 truncate px-2 text-base min-w-0">
              {isUploading 
                ? `Uploading: ${uploadProgress}%` 
                : "Drop file here or click to browse..."}
            </span>
          )}
        </div>
        
        {/* Updated X button positioning */}
        {(hasValidFileValue(value) || hasValidFileValue(lastValidUrl)) && !isUploading && (
          <button
            type="button"
            onClick={handleRemoveFile}
            className="absolute right-[5.5rem] top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-100 text-gray-500 hover:text-red-500 z-10"
            title="Remove file"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        
        {/* Browse button */}
        <span className="absolute right-4 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#F16745] rounded-md text-sm text-white z-10 flex-shrink-0">
          Browse
        </span>
      </div>
      
      {errorMessage && (
        <div className="mt-2 text-sm text-red-500">
          {errorMessage}
        </div>
      )}
      
      {/* Show progress bar even for very fast uploads */}
      {isUploading && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-[#F16745] h-2.5 rounded-full transition-all duration-300 ease-in-out" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadDropzone;